import { useCallback, useEffect, useRef, useState } from 'react'
import { type NavigateFunction, useLocation, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { MOBILE_MEDIA_QUERY } from '@/hooks/use-is-mobile'
import { useUiStore } from '@/stores/ui'
import {
  useCaptchaCode,
  useClientLoginMethods,
  usePasswordLogin,
  useSendSmsCode,
  useSmsLogin,
  useWechatAuthorizeUrl,
  useWechatMpQrCode,
  useWechatMpQrStatus,
  useWechatTicketLogin,
} from '@/features/auth/api'
import {
  acceptAllRequiredPolicies,
  clearLoginPolicyConsent,
  markLoginPolicyConsent,
} from '@/features/policy/api'
import {
  resolvePostLoginRedirect,
  savePostLoginRedirect,
} from '@/features/auth/post-login-redirect'
import type { ClientLoginMethodVo, WechatMpQrCodeVo, WechatQrSceneStatus } from '@/types/api'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Icon } from '@/components/business/icon'
import { useSitePolicyLinks, useSiteTitle } from '@/features/site/api'
import { clearWechatIdentityLinkIntent } from '@/features/auth/wechat-callback-intent'
import { useAiGuestAccessStore } from '@/features/ai-doc/guest-access'

type Tab = 'wechat' | 'sms' | 'password'
type WechatLoginMode = 'web' | 'wechat-qr' | 'mobile-qr'
type LoginSwitchAction = { label: string; onClick: () => void }

const smsSchema = z.object({
  phonenumber: z.string().regex(/^1[3-9]\d{9}$/, '请输入有效的手机号'),
  smsCode: z.string().regex(/^\d{4}$/, '请输入 4 位验证码'),
})
type SmsForm = z.infer<typeof smsSchema>

const passwordSchema = z.object({
  username: z.string().min(2, '账号至少 2 个字符').max(30, '账号最多 30 个字符'),
  password: z.string().min(5, '密码至少 5 个字符').max(30, '密码最多 30 个字符'),
  code: z.string().optional(),
})
type PasswordForm = z.infer<typeof passwordSchema>

const WECHAT_QR_POLL_INTERVAL_MS = 1800
const WECHAT_LOGIN_ICON = '/auth/icons/wechat-login.svg'
const FIGMA_LOGIN_SHELL_HEIGHT_CLASS = 'h-[min(569px,calc(100dvh-1rem))]'
const LOGIN_RENDERER_BY_METHOD = {
  password: 'password',
  sms: 'sms-code',
  wechat: 'wechat-qrcode',
} as const

const loginDialogCss = `
[data-slot="dialog-overlay"] {
  background: rgba(0, 0, 8, 0.56);
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
}

.legal-login-content {
  background: transparent !important;
  box-shadow: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

.legal-login-shell {
  background: #fdfdfd;
  border: 0;
  box-shadow: none;
}

.dark .legal-login-shell {
  background: #fff;
  color: #0e0f14;
}
`

function getWechatLoginMode(): WechatLoginMode {
  if (typeof window === 'undefined') return 'web'

  const ua = window.navigator.userAgent
  const isWechatBrowser = /MicroMessenger/i.test(ua)
  const isMobileUA = /Android|iPhone|iPad|iPod|Mobile/i.test(ua)
  const isCoarsePointer = window.matchMedia?.('(pointer: coarse)').matches ?? false
  const isSmallScreen = window.matchMedia?.(MOBILE_MEDIA_QUERY).matches ?? false

  if (isWechatBrowser) return 'wechat-qr'
  return isMobileUA || (isCoarsePointer && isSmallScreen) ? 'mobile-qr' : 'web'
}

function useWechatLoginMode(): WechatLoginMode {
  const [mode, setMode] = useState<WechatLoginMode>(() => getWechatLoginMode())

  useEffect(() => {
    const update = () => setMode(getWechatLoginMode())
    const pointerQuery = window.matchMedia?.('(pointer: coarse)')
    const widthQuery = window.matchMedia?.(MOBILE_MEDIA_QUERY)

    pointerQuery?.addEventListener('change', update)
    widthQuery?.addEventListener('change', update)
    window.addEventListener('resize', update)

    return () => {
      pointerQuery?.removeEventListener('change', update)
      widthQuery?.removeEventListener('change', update)
      window.removeEventListener('resize', update)
    }
  }, [])

  return mode
}

export function LoginDialog() {
  const [selectedTab, setSelectedTab] = useState<Tab | null>(null)
  const [policyAgreed, setPolicyAgreed] = useState(false)
  const wechatMode = useWechatLoginMode()
  const location = useLocation()
  const navigate = useNavigate()
  const loginModalOpen = useUiStore((s) => s.loginModalOpen)
  const closeLoginModal = useUiStore((s) => s.closeLoginModal)
  const pendingClaimConversationId = useAiGuestAccessStore(
    (state) => state.pendingClaimConversationId,
  )
  const redirectTo = pendingClaimConversationId
    ? `/u/ai-doc?conversationId=${encodeURIComponent(pendingClaimConversationId)}`
    : resolvePostLoginRedirect(location.search)
  const loginMethodsQuery = useClientLoginMethods(loginModalOpen)
  const availableMethods = loginMethodsQuery.isError
    ? []
    : (loginMethodsQuery.data?.methods ?? []).filter(isSupportedLoginMethod)
  const defaultTab = availableMethods.some(
    (method) => method.methodCode === loginMethodsQuery.data?.defaultMethod,
  )
    ? loginMethodsQuery.data?.defaultMethod
    : availableMethods[0]?.methodCode
  const tab = availableMethods.some((method) => method.methodCode === selectedTab)
    ? selectedTab
    : (defaultTab ?? null)
  const isWechatAuthorizePanel = tab === 'wechat' && wechatMode === 'web'
  const headerOffsetClass = isWechatAuthorizePanel ? 'mt-[111px]' : 'mt-[36px]'
  const switchActions = availableMethods
    .filter((method) => method.methodCode !== tab)
    .map((method) => ({
      label: method.displayName,
      onClick: () => setSelectedTab(method.methodCode),
    }))

  const requirePolicyAgreement = useCallback(() => {
    toast.error('请先阅读并勾选同意用户协议和隐私政策')
    return false
  }, [])

  const closeDialog = useCallback(() => {
    setSelectedTab(null)
    setPolicyAgreed(false)
    clearLoginPolicyConsent()
    closeLoginModal()
  }, [closeLoginModal])

  const handlePolicyAgreedChange = useCallback((agreed: boolean) => {
    setPolicyAgreed(agreed)
    if (!agreed) clearLoginPolicyConsent()
  }, [])

  const afterLogin = useCallback(async () => {
    if (policyAgreed) {
      await acceptAllRequiredPolicies().catch(() => {})
    }
    toast.success('登录成功')
    closeDialog()
    navigateAfterLogin(navigate, redirectTo)
  }, [closeDialog, navigate, policyAgreed, redirectTo])

  return (
    <Dialog open={loginModalOpen} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent
        showCloseButton={false}
        className="legal-login-content block max-h-[calc(100dvh-1rem)] w-[636px] max-w-[calc(100vw-1rem)] overflow-hidden rounded-[29px] border-0 bg-transparent p-0 shadow-none focus-visible:outline-none sm:max-h-[min(85vh,85dvh)] sm:max-w-[85vw]"
      >
        <style>{loginDialogCss}</style>
        <div
          data-scrollbar-auto-hide-target="true"
          className={cn(
            'legal-login-shell relative flex max-h-[calc(100dvh-1rem)] w-full flex-col items-center overflow-y-auto rounded-[29px] px-5 sm:max-h-[min(85vh,85dvh)] sm:px-0',
            FIGMA_LOGIN_SHELL_HEIGHT_CLASS,
          )}
        >
          <LoginHeader className={headerOffsetClass} />

          {!tab ? (
            <LoginAvailabilityPanel
              error={loginMethodsQuery.error}
              isLoading={loginMethodsQuery.isPending}
              onRetry={() => void loginMethodsQuery.refetch()}
            />
          ) : tab === 'password' ? (
            <PasswordPanel
              onSuccess={afterLogin}
              onPolicyAgreedChange={handlePolicyAgreedChange}
              policyAgreed={policyAgreed}
              requirePolicyAgreement={requirePolicyAgreement}
              switchActions={switchActions}
            />
          ) : tab === 'sms' ? (
            <SmsPanel
              onSuccess={afterLogin}
              onPolicyAgreedChange={handlePolicyAgreedChange}
              policyAgreed={policyAgreed}
              requirePolicyAgreement={requirePolicyAgreement}
              switchActions={switchActions}
            />
          ) : (
            <WeChatPanel
              active={loginModalOpen && tab === 'wechat'}
              mode={wechatMode}
              onSuccess={afterLogin}
              onPolicyAgreedChange={handlePolicyAgreedChange}
              policyAgreed={policyAgreed}
              redirectTo={redirectTo}
              requirePolicyAgreement={requirePolicyAgreement}
              switchActions={switchActions}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function isSupportedLoginMethod(method: ClientLoginMethodVo): method is ClientLoginMethodVo & {
  methodCode: Tab
} {
  return LOGIN_RENDERER_BY_METHOD[method.methodCode] === method.renderer
}

function LoginAvailabilityPanel({
  error,
  isLoading,
  onRetry,
}: {
  error: Error | null
  isLoading: boolean
  onRetry: () => void
}) {
  return (
    <div className="relative z-10 flex w-full flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
      <Icon
        name={isLoading ? 'autorenew' : 'error'}
        className={cn('text-primary text-3xl', isLoading && 'animate-spin')}
      />
      <p className="text-on-surface-variant text-sm leading-6">
        {isLoading ? '正在获取可用登录方式...' : (error?.message ?? '当前暂无可用登录方式')}
      </p>
      {!isLoading && error ? (
        <button
          type="button"
          onClick={onRetry}
          className="apple-primary rounded-full px-5 py-2 text-sm"
        >
          重新加载
        </button>
      ) : null}
    </div>
  )
}

function LoginHeader({ className }: { className?: string }) {
  const siteTitle = useSiteTitle()

  return (
    <div className={cn('relative z-10 flex flex-col items-center px-5', className)}>
      <DialogTitle className="text-center text-[20px] leading-[49px] font-bold text-black">
        登录{siteTitle}
      </DialogTitle>
    </div>
  )
}

function navigateAfterLogin(navigate: NavigateFunction, target: string): void {
  const replaceWithBrowser = () => {
    try {
      window.location.replace(target)
    } catch {
      window.location.href = '/u/ai-doc'
    }
  }

  try {
    const result = navigate(target, { replace: true })
    if (result && typeof result === 'object' && 'catch' in result) {
      void result.catch(replaceWithBrowser)
    }
  } catch {
    replaceWithBrowser()
  }
}

function WeChatPanel({
  active,
  mode,
  onSuccess,
  onPolicyAgreedChange,
  policyAgreed,
  redirectTo,
  requirePolicyAgreement,
  switchActions,
}: {
  active: boolean
  mode: WechatLoginMode
  onSuccess: () => Promise<void>
  onPolicyAgreedChange: (agreed: boolean) => void
  policyAgreed: boolean
  redirectTo: string
  requirePolicyAgreement: () => boolean
  switchActions: LoginSwitchAction[]
}) {
  const getAuthorizeUrl = useWechatAuthorizeUrl()
  const {
    isPending: isCreatingQrCode,
    mutateAsync: createQrCode,
    reset: resetQrCodeRequest,
  } = useWechatMpQrCode()
  const { mutateAsync: pollQrStatus } = useWechatMpQrStatus()
  const { isPending: isTicketLoginPending, mutateAsync: ticketLogin } = useWechatTicketLogin()
  const [qrCode, setQrCode] = useState<WechatMpQrCodeVo | null>(null)
  const [qrStatus, setQrStatus] = useState<WechatQrSceneStatus>('pending')
  const [loginError, setLoginError] = useState<string | null>(null)
  const requestedRef = useRef(false)
  const pollingRef = useRef(false)
  const handledTicketRef = useRef<string | null>(null)
  const qrRequestIdRef = useRef(0)
  const qrActive = active && mode !== 'web' && policyAgreed

  const requestQrCode = useCallback(() => {
    if (!policyAgreed) {
      requirePolicyAgreement()
      return
    }
    if (!active || mode === 'web') return

    const requestId = qrRequestIdRef.current + 1
    qrRequestIdRef.current = requestId
    clearWechatIdentityLinkIntent()
    savePostLoginRedirect(redirectTo)
    pollingRef.current = false
    handledTicketRef.current = null
    resetQrCodeRequest()
    setQrCode(null)
    setQrStatus('pending')
    setLoginError(null)
    markLoginPolicyConsent()

    void (async () => {
      try {
        const vo = await createQrCode({
          redirect: `${window.location.origin}/auth/wechat-callback`,
        })
        if (qrRequestIdRef.current !== requestId) return
        if (!vo?.sceneId || !vo.qrUrl) {
          setLoginError('二维码响应缺少 qrUrl，请刷新后重试')
          return
        }
        setQrCode(vo)
        setQrStatus('pending')
      } catch (err) {
        if (qrRequestIdRef.current !== requestId) return
        const message = err instanceof Error ? err.message : '二维码生成失败，请稍后重试'
        setLoginError(message)
        toast.error(message)
      }
    })()
  }, [
    active,
    createQrCode,
    mode,
    policyAgreed,
    redirectTo,
    requirePolicyAgreement,
    resetQrCodeRequest,
  ])

  useEffect(() => {
    if (!qrActive) {
      requestedRef.current = false
      pollingRef.current = false
      handledTicketRef.current = null
      return
    }
    if (requestedRef.current) return

    requestedRef.current = true
    requestQrCode()
  }, [qrActive, requestQrCode])

  const completeLogin = useCallback(
    async (ticket: string) => {
      if (!policyAgreed) {
        requirePolicyAgreement()
        return
      }
      if (handledTicketRef.current === ticket) return
      handledTicketRef.current = ticket

      try {
        await ticketLogin(ticket)
      } catch (err) {
        const message = err instanceof Error ? err.message : '微信登录失败，请刷新二维码重试'
        // 登录票据已使用，重试必须重新扫码。
        setQrStatus('consumed')
        setLoginError(message)
        toast.error(message)
        return
      }

      await onSuccess().catch(() => {
        try {
          window.location.replace(redirectTo)
        } catch {
          // 登录态已写入,跳转失败时交给用户刷新当前页继续访问
        }
      })
    },
    [onSuccess, policyAgreed, redirectTo, requirePolicyAgreement, ticketLogin],
  )

  const sceneId = qrCode?.sceneId

  useEffect(() => {
    if (!qrActive || !sceneId || qrStatus !== 'pending') return

    let stopped = false
    const activeSceneId = sceneId

    async function poll() {
      if (pollingRef.current || handledTicketRef.current) return
      pollingRef.current = true

      try {
        const result = await pollQrStatus(activeSceneId)
        if (stopped) return

        setQrStatus(result.status)
        if (result.status === 'pending' || result.status === 'confirmed') {
          setLoginError(null)
        }

        if (result.status === 'confirmed') {
          if (!result.ticket) {
            setLoginError('微信已确认，但后端未返回登录票据')
            return
          }
          await completeLogin(result.ticket)
          return
        }

        if (result.status === 'expired') {
          setLoginError('二维码已过期，请刷新后重新扫码')
        }
        if (result.status === 'consumed') {
          setLoginError('二维码已被使用，请刷新后重新扫码')
        }
      } catch (err) {
        if (!stopped) {
          const message = err instanceof Error ? err.message : '二维码状态查询失败'
          setLoginError(message)
        }
      } finally {
        pollingRef.current = false
      }
    }

    void poll()
    const timer = window.setInterval(() => void poll(), WECHAT_QR_POLL_INTERVAL_MS)
    return () => {
      stopped = true
      window.clearInterval(timer)
    }
  }, [completeLogin, pollQrStatus, qrActive, qrStatus, sceneId])

  const isQrLoading = isCreatingQrCode && !qrCode
  const isCompleting = isTicketLoginPending || qrStatus === 'confirmed'
  const needsRefresh = qrStatus === 'expired' || qrStatus === 'consumed'
  const statusText = getWechatQrStatusText({
    isCompleting,
    isLoading: isQrLoading,
    mode,
    status: qrStatus,
    error: loginError,
  })

  function handleRefreshQrCode() {
    if (!policyAgreed) {
      requirePolicyAgreement()
      return
    }
    requestedRef.current = true
    requestQrCode()
  }

  function handleWechatAuthorize() {
    if (!policyAgreed) {
      requirePolicyAgreement()
      return
    }
    clearWechatIdentityLinkIntent()
    savePostLoginRedirect(redirectTo)
    getAuthorizeUrl.mutate(
      {
        redirect: `${window.location.origin}/auth/wechat-callback`,
        provider: 'wechat_open',
      },
      {
        onSuccess: (vo) => {
          markLoginPolicyConsent()
          window.location.href = vo.authorizeUrl
        },
        onError: (err) => {
          toast.error(err.message)
        },
      },
    )
  }

  if (mode === 'web') {
    return (
      <div className="relative z-10 flex w-full flex-1 flex-col items-center">
        <button
          type="button"
          disabled={getAuthorizeUrl.isPending}
          aria-disabled={!policyAgreed || getAuthorizeUrl.isPending}
          onClick={handleWechatAuthorize}
          className="mt-[36px] flex h-[100px] w-full max-w-[320px] cursor-pointer items-center justify-center gap-[24px] rounded-[10px] bg-[#35cc7f] px-[46px] text-white transition-colors duration-200 hover:bg-[#30c478] disabled:cursor-not-allowed disabled:opacity-70"
        >
          <img src={WECHAT_LOGIN_ICON} alt="" className="h-[61px] w-[68.625px] shrink-0" />
          <span className="flex flex-col items-start text-left">
            <span className="text-[20px] leading-[38px] font-bold">
              {getAuthorizeUrl.isPending ? '准备跳转...' : '微信授权登录'}
            </span>
            <span className="text-[15px] leading-[27px] text-[#d2f4e2]">跳转至微信完成授权</span>
          </span>
        </button>
        <span className="mt-[16px] text-center text-[15px] leading-[27px] text-[#707070]">
          推荐使用微信快捷登录
        </span>

        <PolicyLine
          agreed={policyAgreed}
          className="mt-[50px]"
          onAgreedChange={onPolicyAgreedChange}
        />

        <LoginModeSwitches className="mt-[8px]" actions={switchActions} />
      </div>
    )
  }

  return (
    <div className="relative z-10 flex w-full flex-1 flex-col items-center">
      <div className="mt-7 flex w-full flex-col items-center sm:mt-8">
        <div className="relative flex size-[214px] items-center justify-center rounded-[20px] bg-[rgba(217,217,217,0.24)] p-3">
          <div className="relative flex size-[176px] items-center justify-center overflow-hidden rounded-[16px] bg-white p-3 shadow-[inset_0_0_0_1px_rgba(17,24,39,0.05)]">
            {!policyAgreed ? (
              <div className="flex flex-col items-center gap-3 px-4 text-center">
                <Icon name="shield" className="text-primary text-3xl" />
                <span className="text-on-surface-variant text-xs leading-5">请先勾选同意协议</span>
              </div>
            ) : qrCode?.qrUrl ? (
              <img
                src={qrCode.qrUrl}
                alt="微信扫码登录二维码"
                className={cn(
                  'size-full object-contain transition-opacity duration-200',
                  needsRefresh || isCompleting ? 'opacity-30' : 'opacity-100',
                )}
              />
            ) : loginError ? (
              <div className="flex flex-col items-center gap-3 text-center">
                <Icon name="error" className="text-error text-3xl" />
                <button
                  type="button"
                  onClick={handleRefreshQrCode}
                  className="rounded-[9px] bg-[#007aff] px-4 py-2 text-sm font-medium text-white"
                >
                  刷新二维码
                </button>
              </div>
            ) : (
              <div className="text-primary flex flex-col items-center gap-3">
                <div className="border-primary/20 border-t-primary size-9 animate-spin rounded-full border-4" />
                <span className="text-on-surface-variant text-xs">正在生成二维码</span>
              </div>
            )}

            {(needsRefresh || loginError) && qrCode?.qrUrl ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white/84 px-4 text-center backdrop-blur-sm">
                <Icon
                  name={needsRefresh ? 'refresh' : 'error'}
                  className={cn('text-2xl', needsRefresh ? 'text-primary' : 'text-error')}
                />
                <button
                  type="button"
                  onClick={handleRefreshQrCode}
                  className="rounded-[9px] bg-[#007aff] px-4 py-2 text-sm font-medium text-white"
                >
                  刷新二维码
                </button>
              </div>
            ) : null}
          </div>
        </div>

        <div className="mt-4 flex justify-center text-center text-sm">
          <span className="text-on-surface-variant">
            {policyAgreed ? statusText : '勾选同意后生成微信登录二维码'}
          </span>
        </div>
      </div>

      <PolicyLine agreed={policyAgreed} className="mt-5" onAgreedChange={onPolicyAgreedChange} />

      <LoginModeSwitches className="mt-auto" actions={switchActions} />
    </div>
  )
}

function getWechatQrStatusText({
  error,
  isCompleting,
  isLoading,
  mode,
  status,
}: {
  error: string | null
  isCompleting: boolean
  isLoading: boolean
  mode: WechatLoginMode
  status: WechatQrSceneStatus
}) {
  if (error) return error
  if (isLoading) return '正在生成微信登录二维码'
  if (isCompleting) return '已扫码，正在登录...'
  if (status === 'expired') return '二维码已过期，请刷新后重试'
  if (status === 'consumed') return '二维码已被使用，请刷新后重试'
  return mode === 'wechat-qr' ? '长按识别二维码登录' : '请使用微信扫码登录'
}

function SmsPanel({
  onSuccess,
  onPolicyAgreedChange,
  policyAgreed,
  requirePolicyAgreement,
  switchActions,
}: {
  onSuccess: () => Promise<void>
  onPolicyAgreedChange: (agreed: boolean) => void
  policyAgreed: boolean
  requirePolicyAgreement: () => boolean
  switchActions: LoginSwitchAction[]
}) {
  const [countdown, setCountdown] = useState(0)
  const sendCode = useSendSmsCode()
  const smsLogin = useSmsLogin()

  const form = useForm<SmsForm>({
    resolver: zodResolver(smsSchema),
    defaultValues: { phonenumber: '', smsCode: '' },
  })

  function startCountdown() {
    setCountdown(60)
    const timer = setInterval(() => {
      setCountdown((s) => {
        if (s <= 1) {
          clearInterval(timer)
          return 0
        }
        return s - 1
      })
    }, 1000)
  }

  function handleSendCode() {
    if (!policyAgreed) {
      requirePolicyAgreement()
      return
    }
    const phone = form.getValues('phonenumber')
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      form.setError('phonenumber', { message: '请输入有效的手机号' })
      return
    }
    sendCode.mutate(phone, {
      onSuccess: () => {
        toast.success('验证码已发送, 请查收短信')
        startCountdown()
      },
      onError: (err) => toast.error(err.message),
    })
  }

  function onSubmit(values: SmsForm) {
    if (!policyAgreed) {
      requirePolicyAgreement()
      return
    }
    smsLogin.mutate(values, {
      onSuccess: () => onSuccess(),
      onError: (err) => toast.error(err.message),
    })
  }

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="relative z-10 flex w-full flex-1 flex-col items-center"
    >
      <label className="mt-[35px] w-full max-w-[434px]">
        <span className="mb-[10px] block text-[15px] leading-[22px] text-[#818181]">
          手机号（+86）
        </span>
        <span className="flex h-[72px] items-center rounded-full bg-[rgba(217,217,217,0.4)] px-6">
          <input
            {...form.register('phonenumber')}
            inputMode="tel"
            maxLength={11}
            className="text-on-surface h-10 min-w-0 flex-1 border-0 bg-transparent p-0 text-[15px] outline-none"
          />
        </span>
      </label>
      {form.formState.errors.phonenumber && (
        <span className="text-error mt-1 w-full max-w-[434px] px-1 text-xs">
          {form.formState.errors.phonenumber.message}
        </span>
      )}

      <label className="mt-[27px] w-full max-w-[434px]">
        <span className="mb-[10px] block text-[15px] leading-[22px] text-[#818181]">验证码</span>
        <span className="flex h-[72px] items-center rounded-full bg-[rgba(217,217,217,0.4)] px-6">
          <input
            {...form.register('smsCode')}
            inputMode="numeric"
            maxLength={4}
            className="text-on-surface h-10 min-w-0 flex-1 border-0 bg-transparent p-0 text-[15px] outline-none"
          />
          <button
            type="button"
            disabled={countdown > 0 || sendCode.isPending}
            aria-disabled={!policyAgreed || countdown > 0 || sendCode.isPending}
            onClick={handleSendCode}
            className="shrink-0 px-2 text-right text-[15px] leading-[49px] text-[#007aff] transition-colors disabled:cursor-not-allowed disabled:text-[#818181]"
          >
            {countdown > 0
              ? `重新发送(${countdown}s)`
              : sendCode.isPending
                ? '发送中'
                : '发送验证码'}
          </button>
        </span>
      </label>
      {form.formState.errors.smsCode && (
        <span className="text-error mt-1 w-full max-w-[434px] px-1 text-xs">
          {form.formState.errors.smsCode.message}
        </span>
      )}

      <button
        type="submit"
        disabled={smsLogin.isPending}
        aria-disabled={!policyAgreed || smsLogin.isPending}
        className="mt-[36px] h-[40px] w-[127px] rounded-[9.409px] bg-[#007aff] px-4 text-[15px] leading-[31.364px] font-normal text-white transition duration-150 hover:bg-[#006ee6] disabled:cursor-not-allowed disabled:opacity-50"
      >
        {smsLogin.isPending ? '登录中...' : '登录'}
      </button>

      <PolicyLine
        agreed={policyAgreed}
        className="mt-[24px]"
        onAgreedChange={onPolicyAgreedChange}
      />

      <LoginModeSwitches className="mt-[8px]" actions={switchActions} />
    </form>
  )
}

function PasswordPanel({
  onSuccess,
  onPolicyAgreedChange,
  policyAgreed,
  requirePolicyAgreement,
  switchActions,
}: {
  onSuccess: () => Promise<void>
  onPolicyAgreedChange: (agreed: boolean) => void
  policyAgreed: boolean
  requirePolicyAgreement: () => boolean
  switchActions: LoginSwitchAction[]
}) {
  const { isPending: isCaptchaPending, mutate: fetchCaptcha } = useCaptchaCode()
  const passwordLogin = usePasswordLogin()
  const [captcha, setCaptcha] = useState<{
    enabled: boolean
    uuid?: string
    img?: string
  }>({ enabled: false })

  const form = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { username: '', password: '', code: '' },
  })

  const loadCaptcha = useCallback(() => {
    fetchCaptcha(undefined, {
      onSuccess: (vo) => {
        const enabled = vo?.captchaEnabled !== false
        setCaptcha({
          enabled,
          uuid: vo?.uuid,
          img: vo?.img,
        })
        form.setValue('code', '')
        form.clearErrors('code')
      },
      onError: (err) => {
        toast.error(err.message)
      },
    })
  }, [fetchCaptcha, form])

  useEffect(() => {
    loadCaptcha()
  }, [loadCaptcha])

  function onSubmit(values: PasswordForm) {
    if (!policyAgreed) {
      requirePolicyAgreement()
      return
    }
    if (captcha.enabled && !values.code?.trim()) {
      form.setError('code', { message: '请输入验证码' })
      return
    }
    passwordLogin.mutate(
      {
        username: values.username,
        password: values.password,
        ...(captcha.enabled ? { code: values.code?.trim(), uuid: captcha.uuid } : {}),
      },
      {
        onSuccess: () => onSuccess(),
        onError: (err) => {
          toast.error(err.message)
          if (captcha.enabled) loadCaptcha()
        },
      },
    )
  }

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="relative z-10 flex w-full flex-1 flex-col items-center"
    >
      <label className="mt-7 w-full max-w-[434px] sm:mt-8">
        <span className="mb-2 block text-sm text-[#818181]">账号</span>
        <span className="flex h-[60px] items-center rounded-full bg-[rgba(217,217,217,0.4)] px-6 sm:h-[72px]">
          <input
            {...form.register('username')}
            autoComplete="username"
            placeholder="请输入账号"
            maxLength={30}
            className="text-on-surface placeholder:text-outline h-10 min-w-0 flex-1 border-0 bg-transparent p-0 text-base outline-none"
          />
        </span>
      </label>
      {form.formState.errors.username && (
        <span className="text-error mt-1 w-full max-w-[434px] px-1 text-xs">
          {form.formState.errors.username.message}
        </span>
      )}

      <label className="mt-4 w-full max-w-[434px] sm:mt-5">
        <span className="mb-2 block text-sm text-[#818181]">密码</span>
        <span className="flex h-[60px] items-center rounded-full bg-[rgba(217,217,217,0.4)] px-6 sm:h-[72px]">
          <input
            {...form.register('password')}
            type="password"
            autoComplete="current-password"
            placeholder="请输入密码"
            maxLength={30}
            className="text-on-surface placeholder:text-outline h-10 min-w-0 flex-1 border-0 bg-transparent p-0 text-base outline-none"
          />
        </span>
      </label>
      {form.formState.errors.password && (
        <span className="text-error mt-1 w-full max-w-[434px] px-1 text-xs">
          {form.formState.errors.password.message}
        </span>
      )}

      {captcha.enabled && (
        <>
          <label className="mt-4 w-full max-w-[434px] sm:mt-5">
            <span className="mb-2 block text-sm text-[#818181]">图形验证码</span>
            <span className="flex h-[60px] items-center rounded-full bg-[rgba(217,217,217,0.4)] py-1 pr-2 pl-6 sm:h-[72px]">
              <input
                {...form.register('code')}
                autoComplete="off"
                placeholder="请输入验证码"
                maxLength={8}
                className="text-on-surface placeholder:text-outline h-10 min-w-0 flex-1 border-0 bg-transparent p-0 text-base outline-none"
              />
              <button
                type="button"
                onClick={loadCaptcha}
                disabled={isCaptchaPending}
                aria-label="刷新验证码"
                title="刷新验证码"
                className="border-outline-variant flex h-12 w-[104px] shrink-0 items-center justify-center overflow-hidden rounded-full border bg-white/80 transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-60 sm:h-14"
              >
                {captcha.img ? (
                  <img
                    src={toCaptchaImageSrc(captcha.img)}
                    alt="图形验证码"
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <Icon
                    name="autorenew"
                    className={cn('text-primary text-base', isCaptchaPending && 'animate-spin')}
                  />
                )}
              </button>
            </span>
          </label>
          {form.formState.errors.code && (
            <span className="text-error mt-1 w-full max-w-[434px] px-1 text-xs">
              {form.formState.errors.code.message}
            </span>
          )}
        </>
      )}

      <button
        type="submit"
        disabled={passwordLogin.isPending}
        aria-disabled={!policyAgreed || passwordLogin.isPending}
        className={cn(
          'mt-6 h-12 w-[128px] rounded-[9px] bg-[#007aff] px-4 text-base font-medium text-white transition duration-150 hover:bg-[#006ee6] disabled:cursor-not-allowed disabled:opacity-50 sm:mt-7 sm:h-14',
          !policyAgreed && 'opacity-50',
        )}
      >
        {passwordLogin.isPending ? '登录中...' : '登录'}
      </button>

      <PolicyLine agreed={policyAgreed} className="mt-4" onAgreedChange={onPolicyAgreedChange} />

      <LoginModeSwitches className="mt-auto" actions={switchActions} />
    </form>
  )
}

function toCaptchaImageSrc(value: string): string {
  const image = value.trim()
  return image.startsWith('data:') ? image : `data:image/png;base64,${image}`
}

function ModeSwitchButton({
  children,
  className,
  onClick,
}: {
  children: string
  className?: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex cursor-pointer items-center justify-center px-4 py-2 text-[15px] leading-[27px] text-[#aaa] transition-colors hover:text-[#007aff]',
        className,
      )}
    >
      <span>{children}</span>
    </button>
  )
}

function LoginModeSwitches({
  actions,
  className,
}: {
  actions: Array<{ label: string; onClick: () => void }>
  className?: string
}) {
  return (
    <div className={cn('flex flex-wrap items-center justify-center gap-y-1', className)}>
      {actions.map((action, index) => (
        <span key={action.label} className="inline-flex items-center">
          {index > 0 && <span className="text-[#c7c7c7]">/</span>}
          <ModeSwitchButton className="py-2" onClick={action.onClick}>
            {action.label}
          </ModeSwitchButton>
        </span>
      ))}
    </div>
  )
}

function PolicyLine({
  agreed,
  className,
  onAgreedChange,
}: {
  agreed: boolean
  className?: string
  onAgreedChange: (agreed: boolean) => void
}) {
  const policyLinks = useSitePolicyLinks('login-dialog')

  return (
    <div
      className={cn(
        'relative z-10 flex flex-wrap items-center justify-center gap-x-1 gap-y-1 py-1 text-center text-[15px] leading-[18px] text-[rgba(0,0,0,0.36)]',
        className ?? 'mt-3',
      )}
    >
      <label className="mr-1 inline-flex cursor-pointer items-center gap-1.5">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(event) => onAgreedChange(event.target.checked)}
          className="size-4 shrink-0 cursor-pointer rounded border-black/20 accent-[#007aff]"
        />
        <span>我已阅读并同意</span>
      </label>
      <a
        href={policyLinks.userAgreementHref}
        target="_blank"
        rel="noreferrer"
        className="cursor-pointer text-[#007aff] hover:underline"
      >
        《用户协议》
      </a>
      <span>和</span>
      <a
        href={policyLinks.privacyPolicyHref}
        target="_blank"
        rel="noreferrer"
        className="cursor-pointer text-[#007aff] hover:underline"
      >
        《隐私政策》
      </a>
    </div>
  )
}
